"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import { useSession } from "next-auth/react";
import { Bug, X, RotateCcw, Eraser, Undo2, ChevronRight, Loader2, ExternalLink, CheckCircle2, AlertCircle, Pencil, Check, Camera } from "lucide-react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { useContext } from "react";
import { ThemeContext } from "@/context/ThemeContext";

type ReportType = "bug" | "feature";
type BugPriority = "minor" | "major" | "critical";
type FeaturePriority = "low" | "medium" | "high";

interface Stroke {
  points: { x: number; y: number }[];
}

interface SubmitResponse {
  issueNumber: number;
  issueUrl: string;
}

const BUG_PRIORITIES: { value: BugPriority; label: string }[] = [
  { value: "minor", label: "Minor annoyance" },
  { value: "major", label: "Causing problems" },
  { value: "critical", label: "Site is broken!" },
];

const FEATURE_PRIORITIES: { value: FeaturePriority; label: string }[] = [
  { value: "low", label: "No rush" },
  { value: "medium", label: "Would be nice soon" },
  { value: "high", label: "Really need this" },
];

export default function BugReporter() {
  const { data: session } = useSession();
  const themeCtx = useContext(ThemeContext);
  const themeName = themeCtx?.name || "unknown";
  const [isOpen, setIsOpen] = useState(false);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [screenshotImg, setScreenshotImg] = useState<HTMLImageElement | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [markupMode, setMarkupMode] = useState(false);
  const [reportType, setReportType] = useState<ReportType>("bug");
  const [priority, setPriority] = useState<string>("minor");
  const [description, setDescription] = useState("");
  const [expected, setExpected] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{ success: boolean; data?: SubmitResponse; error?: string } | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [attachedImages, setAttachedImages] = useState<{ name: string; dataUrl: string }[]>([]);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentStrokeRef = useRef<Stroke | null>(null);

  const isAdmin = session?.user?.role === "masteradmin" || session?.user?.role === "admin" || session?.user?.role === "manager";

  // Don't render inside iframes (prevents duplicate in admin preview panel)
  const [isInIframe, setIsInIframe] = useState(false);
  useEffect(() => {
    try {
      if (window.self !== window.top) setIsInIframe(true);
    } catch {
      setIsInIframe(true);
    }
  }, []);

  // Swap elements html2canvas can't render with plain-div clones, capture, then restore
  const patchDomForCapture = useCallback(() => {
    const restorations: (() => void)[] = [];

    // 1. <select> → div showing selected text
    document.querySelectorAll("select").forEach((sel) => {
      const text = sel.options[sel.selectedIndex]?.text || "";
      const rect = sel.getBoundingClientRect();
      const styles = window.getComputedStyle(sel);
      const clone = document.createElement("div");
      clone.textContent = text;
      clone.style.cssText = `
        position:absolute; left:${rect.left + window.scrollX}px; top:${rect.top + window.scrollY}px;
        width:${rect.width}px; height:${rect.height}px; z-index:999999; pointer-events:none;
        display:flex; align-items:center; padding:${styles.padding};
        font:${styles.font}; color:${styles.color}; background:${styles.backgroundColor};
        border:${styles.border}; border-radius:${styles.borderRadius}; box-sizing:border-box;
        overflow:hidden; white-space:nowrap; text-overflow:ellipsis;
      `;
      document.body.appendChild(clone);
      restorations.push(() => clone.remove());
    });

    // 2. Elements with overflow:hidden + text-overflow:ellipsis that clip text
    document.querySelectorAll("[class*=truncate]").forEach((el) => {
      const htmlEl = el as HTMLElement;
      const original = htmlEl.style.overflow;
      const originalWS = htmlEl.style.whiteSpace;
      const originalTE = htmlEl.style.textOverflow;
      // Temporarily remove truncation so html2canvas sees full text
      htmlEl.style.overflow = "visible";
      htmlEl.style.whiteSpace = "normal";
      htmlEl.style.textOverflow = "clip";
      restorations.push(() => {
        htmlEl.style.overflow = original;
        htmlEl.style.whiteSpace = originalWS;
        htmlEl.style.textOverflow = originalTE;
      });
    });

    // 3. Fix elements hidden by CSS animations — hero text, CTAs, etc. start
    // with opacity-0 and animate in. html2canvas captures the pre-animation state.
    document.querySelectorAll("[class*=animate-]").forEach((el) => {
      const htmlEl = el as HTMLElement;
      if (htmlEl.classList?.contains("opacity-0")) {
        const origOpacity = htmlEl.style.opacity;
        htmlEl.style.opacity = "1";
        restorations.push(() => {
          htmlEl.style.opacity = origOpacity;
        });
      }
    });

    // 4. Convert fixed-position elements to absolute so html2canvas captures them
    // at the correct viewport position (html2canvas doesn't handle fixed well)
    document.querySelectorAll("*").forEach((el) => {
      const htmlEl = el as HTMLElement;
      if (htmlEl.id === "bug-reporter-overlay" || htmlEl.id === "bug-reporter-button") return;
      const computed = window.getComputedStyle(htmlEl);
      if (computed.position === "fixed") {
        const rect = htmlEl.getBoundingClientRect();
        const origPosition = htmlEl.style.position;
        const origTop = htmlEl.style.top;
        const origLeft = htmlEl.style.left;
        const origRight = htmlEl.style.right;
        const origBottom = htmlEl.style.bottom;
        htmlEl.style.position = "absolute";
        htmlEl.style.top = `${rect.top + window.scrollY}px`;
        htmlEl.style.left = `${rect.left + window.scrollX}px`;
        htmlEl.style.right = "auto";
        htmlEl.style.bottom = "auto";
        restorations.push(() => {
          htmlEl.style.position = origPosition;
          htmlEl.style.top = origTop;
          htmlEl.style.left = origLeft;
          htmlEl.style.right = origRight;
          htmlEl.style.bottom = origBottom;
        });
      }
    });

    return () => restorations.forEach((fn) => fn());
  }, []);

  // Capture screenshot
  const captureScreenshot = useCallback(async () => {
    setIsCapturing(true);
    try {
      // Patch DOM before capture
      const restore = patchDomForCapture();

      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(document.documentElement, {
        useCORS: true,
        allowTaint: true,
        scale: Math.min(window.devicePixelRatio, 2),
        logging: false,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        width: window.innerWidth,
        height: window.innerHeight,
        x: window.scrollX,
        y: window.scrollY,
        ignoreElements: (element) => {
          return element.id === "bug-reporter-overlay" || element.id === "bug-reporter-button";
        },
      });

      // Restore DOM immediately after capture
      restore();

      const dataUrl = canvas.toDataURL("image/png");
      setScreenshot(dataUrl);
      setStrokes([]);
      setMarkupMode(false);

      const img = new window.Image();
      img.onload = () => {
        setScreenshotImg(img);
      };
      img.src = dataUrl;
    } catch (err) {
      console.error("Failed to capture screenshot:", err);
    } finally {
      setIsCapturing(false);
    }
  }, [patchDomForCapture]);

  // Open modal and capture
  const handleOpen = useCallback(async () => {
    // Capture screenshot FIRST, before opening modal — so dropdowns/menus
    // are still visible and the modal overlay doesn't cover the page
    await captureScreenshot();
    setIsOpen(true);
    setTimeout(() => setShowModal(true), 10);
  }, [captureScreenshot]);

  // Close modal
  const handleClose = useCallback(() => {
    setShowModal(false);
    setTimeout(() => {
      setIsOpen(false);
      setScreenshot(null);
      setScreenshotImg(null);
      setStrokes([]);
      setMarkupMode(false);
      setReportType("bug");
      setPriority("minor");
      setDescription("");
      setExpected("");
      setSubmitResult(null);
      setAttachedImages([]);
    }, 200);
  }, []);

  const handleAttachImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    Array.from(files).forEach((file) => {
      if (!file.type.startsWith("image/")) return;
      if (file.size > 10 * 1024 * 1024) return; // 10MB limit
      const reader = new FileReader();
      reader.onload = () => {
        setAttachedImages((prev) => {
          if (prev.length >= 5) return prev; // max 5
          return [...prev, { name: file.name, dataUrl: reader.result as string }];
        });
      };
      reader.readAsDataURL(file);
    });
    e.target.value = ""; // reset so same file can be re-selected
  };

  // Draw on canvas
  // When drawBackground is true (markup mode), the screenshot is drawn on the canvas.
  // When false (static overlay mode), only strokes are drawn on a transparent canvas.
  const redrawCanvas = useCallback(
    (currentStrokes: Stroke[], activeStroke?: Stroke, drawBackground = true) => {
      const canvas = canvasRef.current;
      if (!canvas || !screenshotImg) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (drawBackground) {
        ctx.drawImage(screenshotImg, 0, 0, canvas.width, canvas.height);
      }

      const allStrokes = activeStroke ? [...currentStrokes, activeStroke] : currentStrokes;

      for (const stroke of allStrokes) {
        if (stroke.points.length < 2) continue;
        ctx.beginPath();
        ctx.strokeStyle = "#ef4444";
        ctx.lineWidth = 3;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
        for (let i = 1; i < stroke.points.length; i++) {
          ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
        }
        ctx.stroke();
      }
    },
    [screenshotImg]
  );

  // Initialize canvas when screenshot is ready or markup mode changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !screenshotImg) return;

    // In desktop fullscreen mode, show at full screenshot size (scrollable container).
    // In inline mode, cap at 440px width.
    const isDesktopFullscreen = markupMode && window.innerWidth >= 768;
    const maxWidth = isDesktopFullscreen ? screenshotImg.width : 440;
    const aspect = screenshotImg.height / screenshotImg.width;
    const displayWidth = Math.min(screenshotImg.width, maxWidth);
    const displayHeight = displayWidth * aspect;
    // In inline mode, cap height. In fullscreen, let it scroll.
    const maxHeight = isDesktopFullscreen ? displayHeight : displayHeight;
    const finalHeight = Math.min(displayHeight, maxHeight);
    const finalWidth = finalHeight < displayHeight ? finalHeight / aspect : displayWidth;

    canvas.width = finalWidth;
    canvas.height = finalHeight;

    // In markup mode, draw screenshot + strokes on canvas.
    // In static mode (overlay canvas for strokes only), draw strokes without background.
    redrawCanvas(strokes, undefined, markupMode);
  }, [screenshotImg, redrawCanvas, strokes, markupMode]);

  const getCanvasPoint = (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if ("touches" in e) {
      const touch = e.touches[0];
      if (!touch) return null;
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      };
    }

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const handlePointerDown = (e: React.MouseEvent | React.TouchEvent) => {
    const point = getCanvasPoint(e);
    if (!point) return;
    setIsDrawing(true);
    currentStrokeRef.current = { points: [point] };
  };

  const handlePointerMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || !currentStrokeRef.current) return;
    const point = getCanvasPoint(e);
    if (!point) return;
    currentStrokeRef.current.points.push(point);
    redrawCanvas(strokes, currentStrokeRef.current);
  };

  const handlePointerUp = () => {
    if (!isDrawing || !currentStrokeRef.current) return;
    setIsDrawing(false);
    const finishedStroke = currentStrokeRef.current;
    currentStrokeRef.current = null;
    if (finishedStroke.points.length > 1) {
      const newStrokes = [...strokes, finishedStroke];
      setStrokes(newStrokes);
      redrawCanvas(newStrokes);
    }
  };

  const handleClearMarkup = () => {
    setStrokes([]);
    redrawCanvas([]);
  };

  const handleUndo = () => {
    setStrokes((prev) => {
      const next = prev.slice(0, -1);
      redrawCanvas(next);
      return next;
    });
  };

  // Submit
  const handleSubmit = async () => {
    if (!description.trim()) return;

    setIsSubmitting(true);
    setSubmitResult(null);

    try {
      let screenshotData = screenshot;

      // If there are strokes, composite screenshot + strokes onto a temp canvas
      if (strokes.length > 0 && screenshotImg) {
        const tempCanvas = document.createElement("canvas");
        const maxWidth = 440;
        const aspect = screenshotImg.height / screenshotImg.width;
        const displayWidth = Math.min(screenshotImg.width, maxWidth);
        const displayHeight = displayWidth * aspect;
        tempCanvas.width = displayWidth;
        tempCanvas.height = displayHeight;
        const tempCtx = tempCanvas.getContext("2d");
        if (tempCtx) {
          tempCtx.drawImage(screenshotImg, 0, 0, displayWidth, displayHeight);
          for (const stroke of strokes) {
            if (stroke.points.length < 2) continue;
            tempCtx.beginPath();
            tempCtx.strokeStyle = "#ef4444";
            tempCtx.lineWidth = 3;
            tempCtx.lineCap = "round";
            tempCtx.lineJoin = "round";
            tempCtx.moveTo(stroke.points[0].x, stroke.points[0].y);
            for (let i = 1; i < stroke.points.length; i++) {
              tempCtx.lineTo(stroke.points[i].x, stroke.points[i].y);
            }
            tempCtx.stroke();
          }
          screenshotData = tempCanvas.toDataURL("image/png");
        }
      }

      // Map priority value to human-readable label for GitHub issue
      const priorityOptions = reportType === "bug" ? BUG_PRIORITIES : FEATURE_PRIORITIES;
      const priorityLabel = priorityOptions.find((p) => p.value === priority)?.label || priority;

      const res = await fetch("/api/bug-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: reportType,
          priority: priorityLabel,
          description: description.trim(),
          expected: reportType === "bug" ? expected.trim() : undefined,
          pageUrl: window.location.href,
          theme: themeName,
          screenshot: screenshotData,
          attachedImages: attachedImages.map((img) => ({ name: img.name, dataUrl: img.dataUrl })),
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to submit report");
      }

      setSubmitResult({ success: true, data });

      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (err) {
      setSubmitResult({
        success: false,
        error: err instanceof Error ? err.message : "Something went wrong",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAdmin || isInIframe) return null;

  return (
    <>
      {/* Floating Button */}
      <button
        id="bug-reporter-button"
        onPointerDown={(e) => { e.preventDefault(); handleOpen(); }}
        className={cn(
          "fixed bottom-[192px] md:bottom-[136px] end-4 z-[9998] flex h-12 w-12 items-center justify-center rounded-full",
          "bg-[#1a1a1a] text-white shadow-lg shadow-black/25",
          "transition-transform duration-200 hover:scale-110",
          "focus:outline-none focus:ring-2 focus:ring-[#b8860b] focus:ring-offset-2"
        )}
        title="Report a bug"
      >
        <Bug className="h-5 w-5" />
        {/* Pulsing dot */}
        <span className="absolute -end-0.5 -top-0.5 flex h-3 w-3">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#b8860b] opacity-75" />
          <span className="relative inline-flex h-3 w-3 rounded-full bg-[#b8860b]" />
        </span>
      </button>

      {/* Modal Overlay */}
      {isOpen && (
        <div
          id="bug-reporter-overlay"
          className={cn(
            "fixed inset-0 z-[9999] flex items-center justify-center p-4",
            "transition-all duration-200",
            showModal ? "bg-black/60 opacity-100" : "bg-black/0 opacity-0"
          )}
          onClick={(e) => {
            if (e.target === e.currentTarget) handleClose();
          }}
        >
          <div
            className={cn(
              "relative max-h-[90vh] w-full max-w-[900px] overflow-y-auto rounded-xl bg-white shadow-2xl",
              "transition-all duration-200",
              showModal ? "scale-100 opacity-100" : "scale-95 opacity-0"
            )}
          >
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-6 py-4 rounded-t-xl">
              <h2 className="text-lg font-semibold text-[#1a1a1a]">
                {reportType === "bug" ? "Report a Bug" : "Request a Feature"}
              </h2>
              <button
                onClick={handleClose}
                className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Success State */}
            {submitResult?.success && submitResult.data && (
              <div className="flex flex-col items-center justify-center gap-4 p-12">
                <CheckCircle2 className="h-16 w-16 text-green-500" />
                <p className="text-lg font-medium text-[#1a1a1a]">
                  Issue #{submitResult.data.issueNumber} created!
                </p>
                <a
                  href={submitResult.data.issueUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[#b8860b] hover:underline"
                >
                  View on GitHub <ExternalLink className="h-4 w-4" />
                </a>
                <p className="text-sm text-gray-400">Closing automatically...</p>
              </div>
            )}

            {/* Main Content */}
            {!submitResult?.success && (
              <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2">
                {/* Left: Screenshot & Markup */}
                <div className="flex flex-col gap-3">
                  <div className="sticky top-[57px] z-[5] bg-white py-2 -mx-1 px-1 flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-700">Screenshot & Markup</h3>
                    <div className="flex gap-1.5">
                      {markupMode && (
                        <>
                          <button
                            onClick={handleUndo}
                            disabled={strokes.length === 0}
                            className={cn(
                              "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors",
                              strokes.length > 0
                                ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                : "bg-gray-50 text-gray-300 cursor-not-allowed"
                            )}
                          >
                            <Undo2 className="h-3 w-3" /> Undo
                          </button>
                          <button
                            onClick={handleClearMarkup}
                            disabled={strokes.length === 0}
                            className={cn(
                              "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors",
                              strokes.length > 0
                                ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                : "bg-gray-50 text-gray-300 cursor-not-allowed"
                            )}
                          >
                            <Eraser className="h-3 w-3" /> Clear
                          </button>
                        </>
                      )}
                      <button
                        onClick={captureScreenshot}
                        disabled={isCapturing}
                        className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200"
                      >
                        <RotateCcw className={cn("h-3 w-3", isCapturing && "animate-spin")} /> Retake
                      </button>
                    </div>
                  </div>

                  <div className="relative rounded-lg border border-gray-200 bg-gray-50 max-h-[50vh] overflow-auto">
                    {isCapturing && (
                      <div className="flex h-64 items-center justify-center">
                        <Loader2 className="h-8 w-8 animate-spin text-[#b8860b]" />
                      </div>
                    )}

                    {/* Static image view (default — no touch interception) */}
                    {!isCapturing && screenshotImg && !markupMode && (
                      <div className="relative">
                        <Image
                          src={screenshot || ""}
                          alt="Screenshot"
                          width={440}
                          height={330}
                          className="w-full"
                          draggable={false}
                          unoptimized
                        />
                        {/* Draw strokes overlay via a non-interactive canvas */}
                        {strokes.length > 0 && (
                          <canvas
                            ref={canvasRef}
                            className="pointer-events-none absolute inset-0 h-full w-full"
                          />
                        )}
                      </div>
                    )}
                    {/* Sticky Edit Markup button (view mode) */}
                    {!isCapturing && screenshotImg && !markupMode && (
                      <div className="sticky bottom-0 z-[5] flex justify-end p-2 bg-gradient-to-t from-gray-100 to-transparent">
                        <button
                          onClick={() => setMarkupMode(true)}
                          className={cn(
                            "inline-flex min-h-[44px] min-w-[44px] items-center gap-1.5",
                            "rounded-lg bg-[#1a1a1a]/80 px-3 py-2 text-sm font-medium text-white",
                            "shadow-lg backdrop-blur-sm transition-colors hover:bg-[#1a1a1a]/90",
                            "active:scale-95"
                          )}
                        >
                          <Pencil className="h-4 w-4" />
                          Edit Markup
                        </button>
                      </div>
                    )}

                    {/* Canvas drawing view (markup mode) — single canvas, different wrapper per breakpoint */}
                    {!isCapturing && screenshotImg && markupMode && (
                      <>
                        {/* Desktop: fullscreen overlay wrapping the shared canvas */}
                        <div className="hidden md:flex fixed inset-0 z-[9999] bg-black/90 flex-col">
                          <div className="flex items-center justify-between px-4 py-3 bg-gray-900">
                            <div className="flex items-center gap-2">
                              <button onClick={handleUndo} disabled={strokes.length === 0} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white/80 bg-white/10 rounded-lg hover:bg-white/20 disabled:opacity-30">Undo</button>
                              <button onClick={handleClearMarkup} disabled={strokes.length === 0} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white/80 bg-white/10 rounded-lg hover:bg-white/20 disabled:opacity-30">Clear</button>
                            </div>
                            <p className="text-xs text-white/60">Draw on the screenshot to highlight the issue</p>
                            <button onClick={() => setMarkupMode(false)} className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 active:scale-95"><Check className="h-4 w-4" /> Done</button>
                          </div>
                          <div className="flex-1 flex items-center justify-center overflow-auto p-4">
                            <canvas
                              ref={canvasRef}
                              className="cursor-crosshair"
                              style={{ touchAction: "pinch-zoom" }}
                              onMouseDown={handlePointerDown}
                              onMouseMove={handlePointerMove}
                              onMouseUp={handlePointerUp}
                              onMouseLeave={handlePointerUp}
                              onTouchStart={handlePointerDown}
                              onTouchMove={handlePointerMove}
                              onTouchEnd={handlePointerUp}
                            />
                          </div>
                        </div>
                        {/* Mobile: inline canvas */}
                        <div className="md:hidden relative">
                          <canvas
                            ref={(el) => { if (el && window.innerWidth < 768) canvasRef.current = el; }}
                            className="w-full cursor-crosshair"
                            style={{ touchAction: "pinch-zoom" }}
                            onMouseDown={handlePointerDown}
                            onMouseMove={handlePointerMove}
                            onMouseUp={handlePointerUp}
                            onMouseLeave={handlePointerUp}
                            onTouchStart={handlePointerDown}
                            onTouchMove={handlePointerMove}
                            onTouchEnd={handlePointerUp}
                          />
                        </div>
                        {/* Mobile: sticky Done button */}
                        <div className="md:hidden sticky bottom-0 z-[5] flex justify-end p-2 bg-gradient-to-t from-gray-100 to-transparent">
                          <button onClick={() => setMarkupMode(false)} className={cn("inline-flex min-h-[44px] min-w-[44px] items-center gap-1.5", "rounded-lg bg-green-600/90 px-3 py-2 text-sm font-medium text-white", "shadow-lg backdrop-blur-sm transition-colors hover:bg-green-700", "active:scale-95")}><Check className="h-4 w-4" /> Done</button>
                        </div>
                      </>
                    )}
                  </div>

                  <p className="text-xs text-gray-400">
                    {markupMode
                      ? "Draw on the screenshot to highlight the issue area"
                      : "Tap \"Edit Markup\" to annotate the screenshot"}
                  </p>
                </div>

                {/* Right: Form */}
                <div className="flex flex-col gap-4">
                  {/* Type Selector */}
                  <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
                    <button
                      onClick={() => {
                        setReportType("bug");
                        setPriority("minor");
                      }}
                      className={cn(
                        "flex-1 rounded-md px-3 py-2 text-sm font-medium transition-all",
                        reportType === "bug"
                          ? "bg-white text-[#1a1a1a] shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      )}
                    >
                      Bug Report
                    </button>
                    <button
                      onClick={() => {
                        setReportType("feature");
                        setPriority("low");
                      }}
                      className={cn(
                        "flex-1 rounded-md px-3 py-2 text-sm font-medium transition-all",
                        reportType === "feature"
                          ? "bg-white text-[#1a1a1a] shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      )}
                    >
                      Feature Request
                    </button>
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">
                      Priority
                    </label>
                    <select
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-[#1a1a1a] focus:border-[#b8860b] focus:outline-none focus:ring-1 focus:ring-[#b8860b]"
                    >
                      {(reportType === "bug" ? BUG_PRIORITIES : FEATURE_PRIORITIES).map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">
                      {reportType === "bug" ? "What's the problem?" : "What's your idea?"}{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={
                        reportType === "bug"
                          ? "Describe what went wrong..."
                          : "Describe the feature you'd like..."
                      }
                      rows={4}
                      className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm text-[#1a1a1a] placeholder-gray-400 focus:border-[#b8860b] focus:outline-none focus:ring-1 focus:ring-[#b8860b]"
                    />
                  </div>

                  {/* Expected Behavior (bugs only) */}
                  {reportType === "bug" && (
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-gray-700">
                        What should happen instead?
                      </label>
                      <textarea
                        value={expected}
                        onChange={(e) => setExpected(e.target.value)}
                        placeholder="Describe the expected behavior..."
                        rows={3}
                        className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm text-[#1a1a1a] placeholder-gray-400 focus:border-[#b8860b] focus:outline-none focus:ring-1 focus:ring-[#b8860b]"
                      />
                    </div>
                  )}

                  {/* Attach Images */}
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">
                      Attach Images <span className="text-xs text-gray-400 font-normal">({attachedImages.length}/5)</span>
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleAttachImages}
                      className="hidden"
                    />
                    <div className="flex flex-wrap gap-2">
                      {attachedImages.map((img, idx) => (
                        <div key={idx} className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200">
                          <img src={img.dataUrl} alt={img.name} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => setAttachedImages((prev) => prev.filter((_, i) => i !== idx))}
                            className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white rounded-bl-lg flex items-center justify-center text-xs hover:bg-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      {attachedImages.length < 5 && (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-[#b8860b] hover:text-[#b8860b] transition-colors"
                        >
                          <Camera className="w-4 h-4" />
                          <span className="text-[9px] mt-0.5">Add</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Current Page URL */}
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">
                      Page URL
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={typeof window !== "undefined" ? window.location.href : ""}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500"
                    />
                  </div>

                  {/* Error State */}
                  {submitResult?.error && (
                    <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                      <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                      <div>
                        <p>{submitResult.error}</p>
                        <button
                          onClick={handleSubmit}
                          className="mt-1 text-xs font-medium underline hover:no-underline"
                        >
                          Try again
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !description.trim()}
                    className={cn(
                      "inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white transition-all",
                      isSubmitting || !description.trim()
                        ? "cursor-not-allowed bg-gray-300"
                        : "bg-[#b8860b] hover:bg-[#a07609] active:scale-[0.98]"
                    )}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit {reportType === "bug" ? "Bug Report" : "Feature Request"}
                        <ChevronRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
